import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { MapPin, Loader2, Navigation, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";

// Place autocomplete. Uses the new `AutocompleteSuggestion` API (recommended
// since March 2025) with a fallback to `AutocompleteService` for projects
// that still rely on the legacy Places API.
const PlaceAutocomplete = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Search location",
  iconColor = "#0B1120",
  testid,
  showCurrentLocation = false,
  componentRestrictions = { country: "in" },
}) => {
  const placesLib = useMapsLibrary("places");
  const geocodingLib = useMapsLibrary("geocoding");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState("");
  const [apiError, setApiError] = useState("");
  const sessionToken = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!placesLib) return;
    try {
      sessionToken.current = new placesLib.AutocompleteSessionToken();
    } catch (err) {
      // Token init failed — autocomplete still works without session billing optimisation
      // eslint-disable-next-line no-console
      console.warn("AutocompleteSessionToken init failed", err);
    }
  }, [placesLib]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fetchSuggestions = useCallback(
    async (input) => {
      if (!placesLib || !input.trim()) {
        setSuggestions([]);
        return;
      }
      setApiError("");
      // ---- 1) Try new API: AutocompleteSuggestion -----------------------
      if (placesLib.AutocompleteSuggestion) {
        try {
          const request = {
            input,
            sessionToken: sessionToken.current,
            includedRegionCodes: [componentRestrictions?.country || "in"],
          };
          const { suggestions: results } =
            await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(
              request
            );
          const mapped = (results || [])
            .filter((s) => s.placePrediction)
            .slice(0, 6)
            .map((s) => {
              const p = s.placePrediction;
              return {
                place_id: p.placeId,
                description: p.text?.toString?.() || p.text?.text || "",
                structured_formatting: {
                  main_text:
                    p.mainText?.toString?.() || p.mainText?.text || "",
                  secondary_text:
                    p.secondaryText?.toString?.() ||
                    p.secondaryText?.text ||
                    "",
                },
                _placePrediction: p, // keep handle so we can call toPlace()
              };
            });
          setSuggestions(mapped);
          return;
        } catch (err) {
          // Fall through to legacy
          // eslint-disable-next-line no-console
          console.warn("AutocompleteSuggestion failed, trying legacy", err);
        }
      }

      // ---- 2) Legacy fallback: AutocompleteService ---------------------
      if (placesLib.AutocompleteService) {
        try {
          const svc = new placesLib.AutocompleteService();
          svc.getPlacePredictions(
            {
              input,
              sessionToken: sessionToken.current,
              componentRestrictions,
              types: ["geocode", "establishment"],
            },
            (preds, status) => {
              if (status === "OK" && preds) {
                setSuggestions(preds.slice(0, 6));
              } else if (status === "REQUEST_DENIED") {
                setApiError("Maps API not enabled. Suggestions unavailable.");
                setSuggestions([]);
              } else {
                setSuggestions([]);
              }
            }
          );
          return;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Legacy autocomplete failed", err);
          setApiError("Maps service unavailable. Please type address manually.");
        }
      }
    },
    [placesLib, componentRestrictions]
  );

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    setOpen(true);
    setHighlight(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 180);
  };

  const fetchDetailsNewAPI = async (pred) => {
    try {
      const place = pred._placePrediction.toPlace();
      await place.fetchFields({
        fields: ["displayName", "formattedAddress", "location"],
      });
      const loc = place.location;
      const lat = typeof loc?.lat === "function" ? loc.lat() : loc?.lat;
      const lng = typeof loc?.lng === "function" ? loc.lng() : loc?.lng;
      return {
        description: pred.description,
        placeId: pred.place_id,
        address: place.formattedAddress || pred.description,
        name: place.displayName || pred.description,
        location: lat != null && lng != null ? { lat, lng } : null,
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Place.fetchFields failed", err);
      return null;
    }
  };

  const fetchDetailsLegacyAPI = (pred) =>
    new Promise((resolve) => {
      try {
        const div = document.createElement("div");
        const svc = new placesLib.PlacesService(div);
        svc.getDetails(
          {
            placeId: pred.place_id,
            fields: ["geometry", "formatted_address", "name"],
            sessionToken: sessionToken.current,
          },
          (place, status) => {
            if (status === "OK" && place?.geometry?.location) {
              resolve({
                description: pred.description,
                placeId: pred.place_id,
                address: place.formatted_address || pred.description,
                name: place.name,
                location: {
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng(),
                },
              });
            } else {
              resolve(null);
            }
          }
        );
      } catch (err) {
        resolve(null);
      }
    });

  const handleSelect = async (pred) => {
    onChange(pred.description);
    setOpen(false);
    let details = null;
    if (pred._placePrediction) {
      details = await fetchDetailsNewAPI(pred);
    }
    if (!details && placesLib?.PlacesService) {
      details = await fetchDetailsLegacyAPI(pred);
    }
    onPlaceSelect?.(
      details || {
        description: pred.description,
        placeId: pred.place_id,
      }
    );
    if (placesLib?.AutocompleteSessionToken) {
      try {
        sessionToken.current = new placesLib.AutocompleteSessionToken();
      } catch (err) {
        // Session token refresh failed — non-fatal
        // eslint-disable-next-line no-console
        console.warn("AutocompleteSessionToken refresh failed", err);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(suggestions.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const detectCurrentLocation = () => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation not supported on this device");
      return;
    }
    if (!geocodingLib && !window.google?.maps?.Geocoder) {
      setGeoError("Map service is still loading…");
      return;
    }
    setGeoError("");
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const geocoder = geocodingLib
          ? new geocodingLib.Geocoder()
          : new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          setGeoLoading(false);
          if (status === "OK" && results?.[0]) {
            const addr = results[0].formatted_address;
            onChange(addr);
            onPlaceSelect?.({
              description: addr,
              address: addr,
              location: { lat, lng },
            });
          } else {
            setGeoError("Could not resolve your address");
          }
        });
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === 1) setGeoError("Permission denied. Allow location access.");
        else setGeoError("Could not get your location. Please try again.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
          style={{ color: iconColor }}
        />
        <Input
          data-testid={testid}
          value={value}
          onChange={handleChange}
          onFocus={() => value && suggestions.length && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="h-12 pl-9 pr-3 rounded-xl border-slate-200 bg-white"
          autoComplete="off"
        />
      </div>

      {showCurrentLocation && (
        <button
          type="button"
          onClick={detectCurrentLocation}
          data-testid="use-current-location-btn"
          className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-[#0B1120] hover:text-amber-700 transition-colors"
        >
          {geoLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Navigation className="h-3 w-3 text-[#F5C518]" />
          )}
          Use My Current Location
        </button>
      )}

      {geoError && (
        <div className="mt-1 flex items-center gap-1 text-[11px] text-red-600">
          <AlertCircle className="h-3 w-3" /> {geoError}
        </div>
      )}
      {apiError && (
        <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-700">
          <AlertCircle className="h-3 w-3" /> {apiError}
        </div>
      )}

      {open && suggestions.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.place_id || `${s.description}-${i}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(s)}
              data-testid={`${testid}-suggestion-${i}`}
              className={`px-3 py-2.5 cursor-pointer flex items-start gap-2 text-sm transition-colors ${
                highlight === i ? "bg-amber-50" : "hover:bg-slate-50"
              }`}
            >
              <MapPin className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-[#0B1120] truncate">
                  {s.structured_formatting?.main_text || s.description}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {s.structured_formatting?.secondary_text}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlaceAutocomplete;
